import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CmlMark } from "./Brand";
import { NAV_LINKS } from "../data/content";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const prefix = pathname === "/" ? "" : "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled || open
                    ? "border-b border-neon/10 bg-noir/90 backdrop-blur-xl"
                    : "border-b border-transparent bg-noir/30 backdrop-blur-sm"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:h-20 sm:px-6">
                <Link to="/" data-testid="header-logo-link" aria-label="Clean My Life — Accueil">
                    <CmlMark />
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.hash}
                            href={`${prefix}${l.hash}`}
                            data-testid={`nav-link-${l.hash.slice(1)}`}
                            className="text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-neon"
                        >
                            {l.label}
                        </a>
                    ))}
                    <Link
                        to="/reservation"
                        data-testid="header-reserve-button"
                        className="rounded-full bg-neon px-5 py-2.5 text-sm font-bold tracking-wide text-noir transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)]"
                    >
                        RÉSERVER
                    </Link>
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                    onClick={() => setOpen(!open)}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-neon/25 text-white lg:hidden"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-nav"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-neon/10 bg-noir/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.hash}
                                    href={`${prefix}${l.hash}`}
                                    data-testid={`mobile-nav-link-${l.hash.slice(1)}`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-xl px-4 py-3 text-base font-medium text-slate-200 transition-colors hover:bg-panel hover:text-neon"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <Link
                                to="/reservation"
                                data-testid="mobile-reserve-button"
                                onClick={() => setOpen(false)}
                                className="mt-3 rounded-full bg-neon px-5 py-3.5 text-center text-base font-bold text-noir"
                            >
                                RÉSERVER EN LIGNE
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
